import axios, {
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";
import { getLanguageIso, t } from "@/lang";
import { ElMessage } from "element-plus";
import { forceRefresh, getAccessToken, login } from "@/services/keycloak";

// Create axios instance
const service: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_APP_BASE_API,
  timeout: 30000,
  headers: {
    "X-Client-Version": import.meta.env.VITE_APP_PIPELINE_ID || "dev",
  },
});

// URLs that don't require authentication
export const noAuthList = [
  /^\/api\/v1\/account_infos\/.*/,
  /^\/api\/v1\/users\/activate\/.*/,
  /^\/api\/v1\/password\/reset\/trigger/,
  /^\/api\/v1\/password\/reset/,
  /^\/api\/v2\/accounts\/register/,
];

// URLs that don't require account context
export const noAccountList = [
  /^\/api\/v1\/users\/.*\/accounts/,
  /^\/api\/v1\/users\/activate\/.*/,
  /^\/api\/v1\/account_infos\/.*/,
  /^\/api\/v1\/password\/reset\/trigger/,
  /^\/api\/v1\/password\/reset/,
  /^\/api\/v2\/accounts\/register/,
];

function needsAuth(url: string | undefined): boolean {
  if (!url) return true;
  return !noAuthList.find((pattern) => url.match(pattern));
}

function needsAccount(url: string | undefined): boolean {
  if (!url) return true;
  return !noAccountList.find((pattern) => url.match(pattern));
}

// Request interceptor
service.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Set language header
    config.headers["Content-Language"] = getLanguageIso();

    // Handle account context
    if (needsAccount(config.url)) {
      try {
        const { useAccountStore } = await import("@/stores/account");
        const accountStore = useAccountStore();
        const account = await accountStore.getCurrentAccount();
        config.headers["Account"] = account.signature;
      } catch {
        // Account not available, continue without it
      }
    }

    // Handle authentication: a Keycloak access token, refreshed with Keycloak when it is about
    // to run out. Without a session the request goes out bare and the 401 below takes over.
    if (needsAuth(config.url)) {
      const token = await getAccessToken();
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor for handling 401 and other errors
service.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized: retry once with a fresh token; when Keycloak has none either,
    // the session is over — sign in again and come back to the current page.
    if (
      error.response?.status === 401 &&
      needsAuth(originalRequest?.url) &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      const token = await forceRefresh();
      if (token) {
        originalRequest.headers["Authorization"] = `Bearer ${token}`;
        return service(originalRequest);
      }
      await login();
    }

    // Handle 403 Forbidden (account-related)
    if (
      error.response?.status === 403 &&
      error.response?.data?.error === "invalid_account"
    ) {
      try {
        const { useAccountStore } = await import("@/stores/account");
        const accountStore = useAccountStore();
        const account = await accountStore.getCurrentAccount(true);
        originalRequest.headers["Account"] = account.signature;
        return service(originalRequest);
      } catch {
        window.location.href = "/accounts";
      }
    }

    // Handle 413 Payload Too Large — upload exceeded the server size limit (100 MB)
    if (error.response?.status === 413) {
      ElMessage.error(t("errors.413.too_large"));
    }

    return Promise.reject(error);
  },
);

// Client version interceptor
service.interceptors.response.use((response) => {
  const serverVersion = response.headers["x-server-version"];
  if (serverVersion) {
    import("@/stores/client").then(({ useClientStore }) => {
      const clientStore = useClientStore();
      clientStore.setLatestVersion(serverVersion);
    });
  }
  return response;
});

export default service;
